/**
 * addressSearch — Geocodificación directa vía CartoCiudad
 *
 * Función autocontenida para buscar una dirección y obtener sus
 * coordenadas EPSG:25830. Pensada para integración en el visor de
 * edición: el usuario escribe una dirección en el EditPanel, se busca,
 * y se posiciona el marcador en las coordenadas resultantes.
 *
 * Flujo: texto dirección → CartoCiudad candidates → WGS84 → EPSG:25830
 *
 * No depende de src/services/geocoding/ (evita conflictos con M3).
 *
 * @module components/map/editing/addressSearch
 * @version 1.0.0
 * @date 2026-02-11
 * @session F8.04 - Búsqueda por dirección en modo edición
 */

import proj4 from 'proj4';

// =============================================================================
// DEFINICIONES PROJ4 (autocontenidas)
// =============================================================================

const EPSG_25830 = 'EPSG:25830';
const EPSG_4326 = 'EPSG:4326';

if (!proj4.defs(EPSG_25830)) {
  proj4.defs(EPSG_25830, '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs');
}
if (!proj4.defs(EPSG_4326)) {
  proj4.defs(EPSG_4326, '+proj=longlat +datum=WGS84 +no_defs +type=crs');
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Endpoint de candidatos CartoCiudad */
const CARTOCIUDAD_CANDIDATES_URL =
  'https://www.cartociudad.es/geocoder/api/geocoder/candidatesJsonp';

/** Endpoint de geocodificación CartoCiudad */
const CARTOCIUDAD_FIND_URL =
  'https://www.cartociudad.es/geocoder/api/geocoder/findJsonp';

/** Timeout por defecto (ms) */
const DEFAULT_TIMEOUT = 5000;

/** Máximo de candidatos a devolver */
const DEFAULT_MAX_CANDIDATES = 5;

// =============================================================================
// TIPOS
// =============================================================================

/** Candidato de dirección devuelto por CartoCiudad */
export interface AddressCandidate {
  /** ID del candidato (para geocodificación posterior) */
  id: string;
  /** Dirección formateada */
  address: string;
  /** Tipo de vía */
  tip_via?: string;
  /** Nombre de la vía */
  streetName?: string;
  /** Número de portal */
  portalNumber?: string;
  /** Municipio */
  municipality?: string;
  /** Provincia */
  province?: string;
  /** Código postal */
  postalCode?: string;
  /** Tipo de resultado ('portal', 'tramo', 'municipio', etc.) */
  type?: string;
  /** Estado (1 = encontrado) */
  state?: number;
}

/** Resultado de geocodificación directa con coordenadas */
export interface AddressGeocodedResult {
  /** Dirección formateada */
  address: string;
  /** Coordenadas EPSG:25830 */
  coords: { x: number; y: number };
  /** Coordenadas WGS84 originales */
  wgs84: { lon: number; lat: number };
  /** Municipio */
  municipality?: string;
  /** Provincia */
  province?: string;
  /** Tipo de localización */
  locationType?: string;
}

/** Error de búsqueda */
export interface AddressSearchError {
  type: 'network' | 'timeout' | 'no-result' | 'empty-query' | 'unknown';
  message: string;
}

/** Opciones de búsqueda */
export interface AddressSearchOptions {
  /** Timeout en ms (default: 5000) */
  timeout?: number;
  /** AbortSignal para cancelación */
  signal?: AbortSignal;
  /** Máximo de candidatos (default: 5) */
  maxCandidates?: number;
  /** Municipio para acotar la búsqueda */
  municipality?: string;
}

// =============================================================================
// CONVERSIÓN
// =============================================================================

/** Convierte WGS84 (lon, lat) → EPSG:25830 (x, y) */
export function fromWGS84(lon: number, lat: number): { x: number; y: number } {
  const [x, y] = proj4(EPSG_4326, EPSG_25830, [lon, lat]);
  return { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
}

// =============================================================================
// HELPERS
// =============================================================================

function buildFetchOptions(
  timeout: number,
  userSignal?: AbortSignal,
): { signal: AbortSignal; timer: ReturnType<typeof setTimeout> } {
  const controller = new AbortController();
  const signal = userSignal
    ? combineAbortSignals(userSignal, controller.signal)
    : controller.signal;
  const timer = setTimeout(() => controller.abort(), timeout);
  return { signal, timer };
}

function combineAbortSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (a.aborted || b.aborted) {
    controller.abort();
    return controller.signal;
  }
  a.addEventListener('abort', abort, { once: true });
  b.addEventListener('abort', abort, { once: true });
  return controller.signal;
}

function handleFetchError(
  err: unknown,
  userSignal?: AbortSignal,
  timeout?: number,
): AddressSearchError {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return userSignal?.aborted
      ? { type: 'network', message: 'Petición cancelada por el usuario' }
      : { type: 'timeout', message: `Timeout tras ${timeout ?? DEFAULT_TIMEOUT}ms` };
  }
  return {
    type: 'network',
    message: err instanceof Error ? err.message : 'Error desconocido',
  };
}

// =============================================================================
// FUNCIÓN: searchCandidates
// =============================================================================

/**
 * Busca candidatos de dirección en CartoCiudad (autocompletado).
 *
 * @param query Texto de búsqueda (dirección parcial o completa)
 * @param options Opciones (timeout, signal, maxCandidates, municipality)
 * @returns Lista de candidatos o error
 */
export async function searchCandidates(
  query: string,
  options: AddressSearchOptions = {},
): Promise<{ ok: true; data: AddressCandidate[] } | { ok: false; error: AddressSearchError }> {
  const trimmed = query.trim();
  if (!trimmed) {
    return { ok: false, error: { type: 'empty-query', message: 'La consulta está vacía' } };
  }

  const timeout = options.timeout ?? DEFAULT_TIMEOUT;
  const limit = options.maxCandidates ?? DEFAULT_MAX_CANDIDATES;

  // Acotar al municipio si se proporciona
  const q = options.municipality
    ? `${trimmed}, ${options.municipality}`
    : trimmed;

  const params = new URLSearchParams({
    q,
    limit: String(limit),
    no_process: '',
  });
  const url = `${CARTOCIUDAD_CANDIDATES_URL}?${params}`;
  const { signal, timer } = buildFetchOptions(timeout, options.signal);

  try {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      return {
        ok: false,
        error: { type: 'network', message: `CartoCiudad respondió ${response.status}` },
      };
    }

    const data = await response.json();
    const candidates: AddressCandidate[] = Array.isArray(data)
      ? data.map((item: Record<string, unknown>) => ({
          id: String(item.id ?? ''),
          address: String(item.address ?? ''),
          tip_via: item.tip_via ? String(item.tip_via) : undefined,
          streetName: item.streetName ? String(item.streetName) : undefined,
          portalNumber: item.portalNumber && item.portalNumber !== 0
            ? String(item.portalNumber)
            : undefined,
          municipality: item.municipality ? String(item.municipality) : undefined,
          province: item.province ? String(item.province) : undefined,
          postalCode: item.postalCode ? String(item.postalCode) : undefined,
          type: item.type ? String(item.type) : undefined,
          state: typeof item.state === 'number' ? item.state : undefined,
        }))
      : [];

    if (candidates.length === 0) {
      return {
        ok: false,
        error: { type: 'no-result', message: `Sin candidatos para "${trimmed}"` },
      };
    }

    return { ok: true, data: candidates };
  } catch (err: unknown) {
    return { ok: false, error: handleFetchError(err, options.signal, timeout) };
  } finally {
    clearTimeout(timer);
  }
}

// =============================================================================
// FUNCIÓN: geocodeAddress
// =============================================================================

/**
 * Geocodifica una dirección completa: texto → coordenadas EPSG:25830.
 *
 * Usa el endpoint /findJsonp de CartoCiudad para obtener coordenadas
 * precisas. El resultado incluye coordenadas tanto en WGS84 como en
 * EPSG:25830 (listas para usar en el visor).
 *
 * @param address Dirección completa o candidato seleccionado
 * @param options Opciones (timeout, signal)
 * @returns Resultado geocodificado o error
 */
export async function geocodeAddress(
  address: string,
  options: AddressSearchOptions = {},
): Promise<{ ok: true; data: AddressGeocodedResult } | { ok: false; error: AddressSearchError }> {
  const trimmed = address.trim();
  if (!trimmed) {
    return { ok: false, error: { type: 'empty-query', message: 'La dirección está vacía' } };
  }

  const timeout = options.timeout ?? DEFAULT_TIMEOUT;
  const q = options.municipality
    ? `${trimmed}, ${options.municipality}`
    : trimmed;

  const params = new URLSearchParams({ q });
  const url = `${CARTOCIUDAD_FIND_URL}?${params}`;
  const { signal, timer } = buildFetchOptions(timeout, options.signal);

  try {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      return {
        ok: false,
        error: { type: 'network', message: `CartoCiudad respondió ${response.status}` },
      };
    }

    const data = await response.json();

    // CartoCiudad devuelve lat/lng directamente
    const lat = data.lat != null ? Number(data.lat) : NaN;
    const lng = data.lng != null ? Number(data.lng) : NaN;

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return {
        ok: false,
        error: { type: 'no-result', message: `Sin coordenadas para "${trimmed}"` },
      };
    }

    const coords = fromWGS84(lng, lat);

    return {
      ok: true,
      data: {
        address: String(data.address ?? trimmed),
        coords,
        wgs84: { lon: lng, lat },
        municipality: data.municipality ? String(data.municipality) : undefined,
        province: data.province ? String(data.province) : undefined,
        locationType: data.type ? String(data.type) : undefined,
      },
    };
  } catch (err: unknown) {
    return { ok: false, error: handleFetchError(err, options.signal, timeout) };
  } finally {
    clearTimeout(timer);
  }
}
